import {Injectable} from '@angular/core';
import IField = Data.FieldManagement.IField;
import IPlantProtectionPlanBook = Data.TaskManagement.IPlantProtectionPlanBook;

@Injectable({providedIn: 'root'})
export class PlantProtectionService {
  /**
   * Filters plant protection applications for a specific field.
   * @param field - The field to get plant protection applications. Must have an Id property.
   * @param ppPlanBooks - Array of plant protection plan books to filter from.
   * @returns Filtered array of plant protection plan books that match the field ID and have a BookedAt value.
   * Returns an empty array if the field parameter is falsy.
   * @public
   */
  public getPpApplications(field: IField, ppPlanBooks: IPlantProtectionPlanBook[]): IPlantProtectionPlanBook[] {
    if (!field) {
      return [];
    }
    return ppPlanBooks.filter(pb => !!pb && pb.FieldId === field.Id && !!pb.BookedAt);
  }

  /**
   * Calculates a slope factor for application rate based on water average, slope, and nitrogen uptake
   * This method determines the calculation slope by:
   * - Checking if all input parameters are valid finite numbers
   * - Returning 0 if slope invalid or undefined or slope value if slope is present but there is no avgWater or nUptake
   * - Computing slope as: (Average Water * Slope) / Nitrogen Uptake
   * Typical use cases include adjusting application rates in precision agriculture
   * based on water availability, crop-specific slope, and nitrogen uptake
   * @param slope Control factor or slope value (e.g., from application settings)
   * @param AvgWater Average water availability or water rate
   * @param nUptake Nitrogen uptake value from crop scans or measurements
   * @returns Calculated slope factor, or 0 if inputs are invalid
   */
  public getSlopeForCalculation(slope?: number, AvgWater?: number, nUptake?: number): number {
    if (!Number.isFinite(slope)) {
      return 0;
    }
    if (!Number.isFinite(AvgWater) || !Number.isFinite(nUptake)) {
      return slope;
    }
    return AvgWater * slope / nUptake;
  }
}
